"use client";

import { useCallback, useEffect, useState } from "react";
import type { IStockItem } from "../../../interfaces/StockList.interface";
import {
  type IndicesPageResult,
  getGlobalIndicesData,
  getSectorIndicesData,
} from "../../../services/TradingMallService";

export interface GlobalIndicesInitialTabData {
  globalIndices?: IndicesPageResult;
  sectorIndices?: IndicesPageResult;
}

interface UseGlobalIndicesListingOptions {
  initialRows?: IStockItem[];
  initialTabData?: GlobalIndicesInitialTabData;
  defaultTab?: string;
  limit?: number;
}

interface UseGlobalIndicesListingResult {
  selectedTab: string;
  setSelectedTab: (value: string) => void;
  rows: IStockItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  showMore: boolean;
  handleShowMore: () => void;
}

interface FetchIndicesPageResult {
  rows: IStockItem[];
  showMore: boolean;
}

function isGlobalTab(value: string): boolean {
  return value.toLowerCase().includes("global");
}

function getInitialTabState({
  tab,
  limit,
  initialRows,
  initialTabData,
}: {
  tab: string;
  limit: number;
  initialRows: IStockItem[];
  initialTabData?: GlobalIndicesInitialTabData;
}): {
  hasSeededData: boolean;
  rows: IStockItem[];
  showMore: boolean;
} {
  const isGlobal = isGlobalTab(tab);
  const seededTabData = isGlobal
    ? initialTabData?.globalIndices
    : initialTabData?.sectorIndices;

  if (seededTabData) {
    const seededRows = Array.isArray(seededTabData.rows) ? seededTabData.rows : [];
    return {
      hasSeededData: true,
      rows: seededRows,
      showMore:
        typeof seededTabData.showMore === "boolean"
          ? seededTabData.showMore
          : seededRows.length >= limit,
    };
  }

  if (isGlobal && initialRows.length > 0) {
    return {
      hasSeededData: true,
      rows: initialRows,
      showMore: initialRows.length >= limit,
    };
  }

  return {
    hasSeededData: false,
    rows: [],
    showMore: false,
  };
}

async function fetchIndicesPageFromServer({
  tab,
  page,
  limit,
}: {
  tab: string;
  page: number;
  limit: number;
}): Promise<FetchIndicesPageResult> {
  return isGlobalTab(tab)
    ? getGlobalIndicesData(page, limit)
    : getSectorIndicesData(page, limit);
}

export function useGlobalIndicesListing({
  initialRows = [],
  initialTabData,
  defaultTab = "",
  limit = 10,
}: UseGlobalIndicesListingOptions): UseGlobalIndicesListingResult {
  const initialState = getInitialTabState({
    tab: defaultTab,
    limit,
    initialRows,
    initialTabData,
  });
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [rows, setRows] = useState<IStockItem[]>(initialState.rows);
  const [page, setPage] = useState(
    initialState.hasSeededData && initialState.rows.length > 0 ? 1 : 0,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showMore, setShowMore] = useState(initialState.showMore);

  const fetchPage = useCallback(
    async (tabValue: string, nextPage: number) => {
      return fetchIndicesPageFromServer({
        tab: tabValue,
        page: nextPage,
        limit,
      });
    },
    [limit],
  );

  useEffect(() => {
    if (!selectedTab) {
      setRows([]);
      setPage(0);
      setShowMore(false);
      return;
    }

    const seededState = getInitialTabState({
      tab: selectedTab,
      limit,
      initialRows,
      initialTabData,
    });
    if (seededState.hasSeededData) {
      setRows(seededState.rows);
      setPage(seededState.rows.length > 0 ? 1 : 0);
      setShowMore(seededState.showMore);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setRows([]);
    setPage(0);
    setShowMore(false);

    void fetchPage(selectedTab, 1)
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
        setPage(1);
        setShowMore(result.showMore);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setPage(0);
        setShowMore(false);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchPage, initialRows, initialTabData, limit, selectedTab]);

  const handleShowMore = useCallback(() => {
    if (isLoading || isLoadingMore || !showMore) {
      return;
    }

    const nextPage = page + 1;
    setIsLoadingMore(true);

    void fetchPage(selectedTab, nextPage)
      .then((result) => {
        setRows((prev) => [...prev, ...result.rows]);
        setPage(nextPage);
        setShowMore(result.showMore);
      })
      .catch(() => {
        setShowMore(false);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [fetchPage, isLoading, isLoadingMore, page, selectedTab, showMore]);

  return {
    selectedTab,
    setSelectedTab,
    rows,
    isLoading,
    isLoadingMore,
    showMore,
    handleShowMore,
  };
}

export default useGlobalIndicesListing;
